// deps
import React, { useCallback, memo, useMemo } from "react";

// internal deps
import { format } from "@swiggy-private/js-utils";
import { isEmpty, useEffectOnce, RightArrow } from "@swiggy-private/core-ui";

// components
import OfferBadge from "./OfferBadge";

// interfaces
import { RenderingDetails } from "@swiggy-private/cart-types";
import { ONE_WIDGET_TEST_IDS } from "@super-ui/test-ids/one-widget";

// helpers
import { getPrefixedTestId } from "@super-ui/helpers/String";
import classNames from "classnames";
import { UX4_PRIMARY_ORANGE, UX4_PRIMARY_BLACK } from "@super-ui/constants/Color";

import styles from "../styles/PurchasePlan.v4.scss";
import { PRICE_WITH_SYMBOL } from "@super-ui/constants";

export interface PurchasePlanProps {
    itemData: RenderingDetails;
    impressionSuperNudge?: (title: string, ctaText: string, subtitle?: string, ctaLink?: string) => void;
    impressionSuperOfferBadge?: (offerText: string) => void;
    onItemClick?: (itemType: string, itemData?: RenderingDetails) => void;
    offerBadgeText?: string;
    testIdPrefix?: string;
    isOneTapPlan?: boolean;
    price?: {
        basePrice: string;
        finalPrice: string;
    };
}

const PurchasePlan: React.FC<PurchasePlanProps> = ({
    itemData,
    onItemClick,
    price,
    offerBadgeText,
    testIdPrefix,
    isOneTapPlan,
    impressionSuperNudge,
    impressionSuperOfferBadge,
}: PurchasePlanProps) => {
    const { display_text: displayText, meta: { cta = null, subtitles = [] } = {} } = itemData;
    const isPriceEmpty = isEmpty(price);

    const onCTAClick = useCallback(() => {
        if (typeof onItemClick === "function") {
            onItemClick(itemData.key?.toUpperCase(), itemData);
        }
    }, [itemData, onItemClick]);

    const [buttonTestId, badgeTestId]: string[] = useMemo(
        () => [
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.PURCHASE_PLAN, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.BADGE, testIdPrefix),
        ],
        [testIdPrefix],
    );

    const showSubtitles = useMemo(() => subtitles.length !== 0, [subtitles]);

    const ctaStyle = useMemo(() => {
        return classNames({
            [styles.cta]: true,
            [styles.ctaOnly]: !showSubtitles,
            [styles.ctaWithPrice]: !isPriceEmpty,
        });
    }, [isPriceEmpty, showSubtitles]);

    const subtitleStyle = useMemo(() => {
        return classNames({
            [styles.subtitle]: true,
            [styles.subtitleWithPrice]: !isPriceEmpty,
        });
    }, [isPriceEmpty]);

    useEffectOnce(() => {
        if (!isEmpty(cta)) {
            impressionSuperNudge?.(displayText, cta.text, subtitles?.[0], cta?.link || "-");
        }
    });

    const basePrice = format(PRICE_WITH_SYMBOL, price?.basePrice);
    const finalPrice = format(PRICE_WITH_SYMBOL, price?.finalPrice);
    const shouldShowBasePrice = price?.basePrice !== price?.finalPrice;

    return (
        <div className={styles.purchasePlanContainer}>
            {showSubtitles && !isEmpty(subtitles) && <div className={subtitleStyle}>{subtitles[0]}</div>}
            {!isEmpty(price) && (
                <div className={styles.price}>
                    {shouldShowBasePrice && <div className={styles.priceBasePrice}>{basePrice}</div>}
                    <div className={styles.priceFinalPrice}>{finalPrice}</div>
                </div>
            )}

            <div className={styles.containerCta}>
                {!isEmpty(cta) && (
                    <button className={ctaStyle} onClick={onCTAClick} aria-label={cta.text} data-testid={buttonTestId}>
                        {cta.text}
                        <RightArrow
                            strokeWidth={2}
                            className={styles.ctaArrow}
                            color={isPriceEmpty ? UX4_PRIMARY_ORANGE : UX4_PRIMARY_BLACK}
                        />
                    </button>
                )}
                {!isEmpty(offerBadgeText) && (
                    <OfferBadge
                        text={offerBadgeText || ""}
                        impressionSuperOfferBadge={impressionSuperOfferBadge}
                        testId={badgeTestId}
                        isOneTapPlan={isOneTapPlan}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(PurchasePlan);
